import { LogoOidc } from "components/icons/generated";
import BadgeNext from "ds/components/BadgeNext";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tooltip from "ds/components/Tooltip";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import setLocation from "shared/setLocation";
import { OidcSettings } from "types/generated";
import ActionCard from "ds/components/ActionCard";

import { openSingleSignOnDrawer } from "../components/Drawer";
import SettingsSingleSignOnCreateOIDC from "./Create";
import SettingsSingleSignOnDetailsOIDC from "./Details";
import SettingsSingleSignOnEditOIDC from "./Edit";
import { showDisableConfirmation } from "./Disable";

type SettingsSingleSignOnOIDCProps = {
  integration: OidcSettings | undefined;
  hasManagedUsers: boolean;
  samlAlreadyEnabled: boolean;
};

const SettingsSingleSignOnOIDC = ({
  integration,
  hasManagedUsers,
  samlAlreadyEnabled,
}: SettingsSingleSignOnOIDCProps) => {
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationSSO,
    defaultCallbackTrackProperties: { method: "OIDC" },
  });

  const isIntegrationActive = !!integration?.adminLogin;

  const onPress = () => {
    openSingleSignOnDrawer({
      title: "Set up OIDC",
      children: ({ setIsDirty }) => <SettingsSingleSignOnCreateOIDC setIsDirty={setIsDirty} />,
    });
    trackSegmentAnalyticsEvent("Set Up Click");
  };

  const handleDisable = () => {
    showDisableConfirmation({ hasManagedUsers });
    trackSegmentAnalyticsEvent("Disable Click");
  };

  const handleActivate = () => {
    setLocation("/oidc/start");
    trackSegmentAnalyticsEvent("Activate Click");
  };

  const handleShowDetails = () => {
    if (integration) {
      openSingleSignOnDrawer({
        title: "OIDC SSO",
        children: () => <SettingsSingleSignOnDetailsOIDC integration={integration} />,
        isDismissable: true,
      });
    }
  };

  const handleEdit = () => {
    if (integration) {
      openSingleSignOnDrawer({
        title: "Edit OIDC SSO",
        children: ({ setIsDirty }) => (
          <SettingsSingleSignOnEditOIDC integration={integration} setIsDirty={setIsDirty} />
        ),
        isDismissable: true,
      });
    }
    trackSegmentAnalyticsEvent("Edit Click");
  };

  return (
    <ActionCard
      title="OIDC"
      description="Connect your Spacelift account to an OIDC supporting identity provider."
      icon={LogoOidc}
      actionComponent={
        <Box gap="medium">
          {/* Regular "Set up" flow */}
          {!integration && !samlAlreadyEnabled && (
            <Button variant="secondary" onPress={onPress}>
              Set up
            </Button>
          )}

          {/* Disabled "Set up" button because SAML is already enabled */}
          {!integration && samlAlreadyEnabled && (
            <Tooltip
              active
              on={(props) => (
                <Button variant="secondary" disabled {...props}>
                  Set up
                </Button>
              )}
            >
              SAML integration already present, can't set up OIDC
            </Tooltip>
          )}

          {integration && !isIntegrationActive && (
            <Button variant="primary" onPress={handleActivate}>
              Activate
            </Button>
          )}

          {integration && (
            <DropdownMenuEllipsis tooltip="OIDC actions" dotsSize="medium">
              <DropdownMenuItem onAction={handleShowDetails}>See details</DropdownMenuItem>
              <DropdownMenuItem onAction={handleEdit}>Edit</DropdownMenuItem>
              <DropdownMenuItem onAction={handleDisable} danger>
                Unlink
              </DropdownMenuItem>
            </DropdownMenuEllipsis>
          )}
        </Box>
      }
      titleSuffix={
        integration &&
        isIntegrationActive && <BadgeNext variant="green" type="strong" text="Active" />
      }
    />
  );
};

export default SettingsSingleSignOnOIDC;
